"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { AppShell } from "@/components/layout/app-shell";
import { useAuth } from "@/hooks/use-auth";
import { useDashboard } from "@/hooks/use-queries";
import { useSocket } from "@/hooks/use-socket";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LeadTypeBadge } from "@/components/lead-type-badge";
import { TrendingUp, Users, CheckCircle2, ArrowUpRight, XCircle, Clock, AlertTriangle, Copy } from "lucide-react";
import { cn } from "@/lib/utils";

// ── Count-up animation hook ──────────────────────────────────────────────────

function useCountUp(target: number, duration = 900) {
  const [count, setCount] = useState(0);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!target) { setCount(0); return; }

    // Respect prefers-reduced-motion
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) { setCount(target); return; }

    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
      setCount(Math.round(eased * target));
      if (p < 1) frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
  }, [target, duration]);

  return count;
}

function AnimatedNumber({ value, suffix }: { value: number; suffix?: string }) {
  const animated = useCountUp(value);
  return <>{suffix ? `${animated}${suffix}` : animated}</>;
}

// ── Config ───────────────────────────────────────────────────────────────────

const KPI_CONFIG = [
  {
    key: "total_conversations",
    title: "Conversaciones Totales",
    icon: Users,
    href: "/leads",
    accent: "text-blue-600",
    ring: "ring-blue-200",
    bg: "bg-blue-50",
  },
  {
    key: "qualified",
    title: "Leads Cualificados",
    icon: CheckCircle2,
    href: "/leads?lead_type=qualified",
    accent: "text-emerald-600",
    ring: "ring-emerald-200",
    bg: "bg-emerald-50",
  },
  {
    key: "conversion_rate",
    title: "Tasa de Conversión",
    icon: TrendingUp,
    href: undefined,
    accent: "text-violet-600",
    ring: "ring-violet-200",
    bg: "bg-violet-50",
    suffix: "%",
  },
  {
    key: "not_qualified",
    title: "No Cualificados",
    icon: XCircle,
    href: "/leads?lead_type=not_qualified",
    accent: "text-red-600",
    ring: "ring-red-200",
    bg: "bg-red-50",
  },
  {
    key: "incomplete_active",
    title: "Incompletos Activos",
    icon: Clock,
    href: "/leads?lead_type=incomplete_active",
    accent: "text-sky-600",
    ring: "ring-sky-200",
    bg: "bg-sky-50",
  },
  {
    key: "duplicate",
    title: "Duplicados",
    icon: Copy,
    href: "/leads?lead_type=duplicate",
    accent: "text-slate-500",
    ring: "ring-slate-200",
    bg: "bg-slate-50",
  },
  {
    key: "active_alerts",
    title: "Alertas Activas",
    icon: AlertTriangle,
    href: "/alerts",
    accent: "text-orange-600",
    ring: "ring-orange-200",
    bg: "bg-orange-50",
  },
];

// ── Page ─────────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const { isLoading: authLoading } = useAuth();
  useSocket();
  const { data: stats, isLoading } = useDashboard();

  if (authLoading) return null;

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Panel</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Resumen de cualificación de leads — últimos 30 días
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-12 gap-4">

          {/* ── Row 1 & 2: KPI cards (3 cols each = 4 per row, 2 rows) ── */}
          {isLoading
            ? Array.from({ length: 8 }).map((_, i) => (
                <Card key={i} className="col-span-3">
                  <CardContent className="p-5">
                    <Skeleton className="h-4 w-24 mb-3" />
                    <Skeleton className="h-8 w-16" />
                  </CardContent>
                </Card>
              ))
            : KPI_CONFIG.map((cfg) => {
                const rawValue =
                  typeof stats?.[cfg.key as keyof typeof stats] === "number"
                    ? (stats[cfg.key as keyof typeof stats] as number)
                    : 0;
                const Icon = cfg.icon;

                const card = (
                  <Card
                    className={cn(
                      "h-full transition-all duration-200",
                      cfg.href
                        ? "hover:shadow-md hover:ring-1 hover:ring-primary/25 hover:-translate-y-0.5 cursor-pointer"
                        : ""
                    )}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            {cfg.title}
                          </p>
                          <p className={`mt-2 text-3xl font-bold tabular-nums font-display ${cfg.accent}`}>
                            <AnimatedNumber value={rawValue} suffix={(cfg as any).suffix} />
                          </p>
                        </div>
                        <div
                          className={`flex h-9 w-9 items-center justify-center rounded-lg ${cfg.bg} ring-1 ${cfg.ring}`}
                        >
                          <Icon className={`h-4 w-4 ${cfg.accent}`} />
                        </div>
                      </div>
                      {cfg.href && (
                        <div className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
                          <span>Ver leads</span>
                          <ArrowUpRight className="h-3 w-3" />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );

                return cfg.href ? (
                  <Link key={cfg.key} href={cfg.href} className="col-span-3">
                    {card}
                  </Link>
                ) : (
                  <div key={cfg.key} className="col-span-3">
                    {card}
                  </div>
                );
              })}

          {/* ── Row 3: Lead Breakdown (7) + Last 7 Days (5) ── */}

          {/* Lead Breakdown — wider */}
          <Card className="col-span-7">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Distribución de Leads
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-9 w-full rounded-md" />
                  ))}
                </div>
              ) : stats?.breakdown?.length ? (
                <div className="space-y-1">
                  {stats.breakdown.map((row) => (
                    <Link
                      key={row.lead_type}
                      href={`/leads?lead_type=${row.lead_type}`}
                      className="flex items-center justify-between rounded-md px-3 py-2 -mx-3 hover:bg-accent/60 transition-colors group"
                    >
                      <LeadTypeBadge type={row.lead_type} />
                      <div className="flex items-center gap-3 text-sm">
                        <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full rounded-full bg-primary/60 transition-all"
                            style={{ width: `${Math.min(row.percentage, 100)}%` }}
                          />
                        </div>
                        <span className="font-semibold tabular-nums w-6 text-right">
                          {row.count}
                        </span>
                        <span className="text-muted-foreground w-10 text-right tabular-nums">
                          {row.percentage}%
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  Sin datos aún. Los leads aparecerán aquí.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Last 7 Days — narrower */}
          <Card className="col-span-5">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Últimos 7 Días
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {isLoading ? (
                <Skeleton className="h-40 w-full" />
              ) : (() => {
                const last7 = stats?.daily_series?.slice(-7) ?? [];
                const hasActivity = last7.some((d) => d.conversations > 0);
                if (!last7.length || !hasActivity) {
                  return (
                    <p className="py-6 text-center text-sm text-muted-foreground">
                      Sin actividad en los últimos 7 días.
                    </p>
                  );
                }
                return (
                  <div className="space-y-1">
                    {last7.map((day) => {
                      const label = new Date(day.date + "T12:00:00").toLocaleDateString("es-ES", {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      });
                      const pct = day.conversations > 0
                        ? Math.round((day.qualified / day.conversations) * 100)
                        : 0;
                      return (
                        <div
                          key={day.date}
                          className="flex items-center justify-between rounded-md px-3 py-2 -mx-3 text-sm"
                        >
                          <span className="text-muted-foreground capitalize w-28 truncate">{label}</span>
                          <div className="flex items-center gap-4">
                            <span className="text-muted-foreground tabular-nums">
                              {day.conversations}
                              <span className="ml-1 text-xs">conv</span>
                            </span>
                            <span className="font-semibold tabular-nums text-emerald-600 w-16 text-right">
                              {day.qualified}
                              <span className="ml-1 text-xs font-normal text-muted-foreground">
                                cual
                              </span>
                            </span>
                            {pct > 0 && (
                              <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
                                {pct}%
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </CardContent>
          </Card>

        </div>
      </div>
    </AppShell>
  );
}
